import { useMemo } from 'react';
import { useAppSelector } from '../redux/helper';
import { RootState } from '../redux/types';

export type ServiceDisplayType = 'bar' | 'kitchen' | null;

export interface UseShowServiceDisplayResult {
  showservicedisplay: boolean;
  serviceDisplayType: ServiceDisplayType;
}

/**
 * Shared hook to determine if the current employee should see service display UI
 * (bar/kitchen order display). Used by HomeScreen, MainScreenTabNavigator,
 * QuickActionsSettings, SettingsScreen.
 */
const useShowServiceDisplay = (): UseShowServiceDisplayResult => {
  const employeedata = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const business = useAppSelector(
    (state: RootState) => state.auth.inApp.account.businessInfo,
  );

  return useMemo(() => {
    const role = employeedata?.bp_employee_role?.toLowerCase?.() || '';
    const normalRoles = ['cashier', 'manager', 'owner', 'supervisor', 'waiter'];
    if (normalRoles.some(normalRole => role.includes(normalRole))) {
      return { showservicedisplay: false, serviceDisplayType: null };
    }
    const barRoles = ['bartender', 'mixologist', 'barista', 'sommelier'];
    const kitchenRoles = [
      'chef',
      'cook',
      'baker',
      'butcher',
      'kitchen',
      'sous chef',
      'pastry chef',
    ];
    const isBarRole = barRoles.some(barRole => role.includes(barRole));
    const isKitchenRole = kitchenRoles.some(kitchenRole =>
      role.includes(kitchenRole),
    );
    if (isBarRole || isKitchenRole) {
      return {
        showservicedisplay: true,
        serviceDisplayType: isBarRole ? 'bar' : 'kitchen',
      };
    }
    const rawType = (
      (business as any)?.bp_business_type ||
      (business as any)?.type ||
      ''
    ).trim();
    const type = rawType.toLowerCase();
    const isKitchen =
      type.includes('hotel') ||
      type.includes('restaurant') ||
      type.includes('bakery') ||
      type.includes('coffee') ||
      type.includes('cafe');
    const isBar =
      type.includes('bar') || type.includes('pub') || type.includes('club');
    if (isKitchen || isBar) {
      return {
        showservicedisplay: true,
        serviceDisplayType: isBar ? 'bar' : 'kitchen',
      };
    }
    const isServiceRole =
      role === 'baker' ||
      role === 'chef' ||
      role === 'butcher' ||
      role === 'bartender' ||
      role === 'mixologist';
    return {
      showservicedisplay: isServiceRole,
      serviceDisplayType: isServiceRole ? 'kitchen' : null,
    };
  }, [employeedata?.bp_employee_role, business]);
};

export default useShowServiceDisplay;
